import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Link,
  Alert,
  InputAdornment,
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Lock,
  ArrowBack,
} from "@mui/icons-material";

import logo from "../../assets/nwt.png";
import { useResetPasswordMutation } from "../../services";
import { notify } from "../../components/Notification";
import { ResetPasswordProps } from "./types";
import {
  BrandIcon,
  BrandSection,
  ContentWrapper,
  Card,
  Container,
  CardSection,
  StyledTextField,
} from "./styles";
import Button, { ButtonTypes } from "../../components/Button";
import { IconButton } from "../../components/IconButton";

interface PasswordStrength {
  score: number;
  feedback: string[];
  color: "error" | "warning" | "info" | "success";
  label: string;
}

export const ResetPasswordPage: React.FC<ResetPasswordProps> = ({ token }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [resetPassword] = useResetPasswordMutation();

  const getPasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    const feedback: string[] = [];

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push("At least 8 characters");
    }

    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Mix of uppercase & lowercase");
    }

    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push("At least one number");
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
    } else {
      feedback.push("At least one special character");
    }

    const strengthMap = {
      0: { color: "error" as const, label: "Very Weak" },
      1: { color: "error" as const, label: "Weak" },
      2: { color: "warning" as const, label: "Fair" },
      3: { color: "info" as const, label: "Good" },
      4: { color: "success" as const, label: "Strong" },
    };

    return {
      score,
      feedback,
      ...strengthMap[score as keyof typeof strengthMap],
    };
  };

  const passwordStrength = getPasswordStrength(password);
  const isPasswordValid = passwordStrength.score >= 3;
  const doPasswordsMatch =
    password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    // Validate token on component mount
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validation
    if (!password || !confirmPassword) {
      setError("Please fill in all fields");
      setLoading(false);
      return;
    }

    if (!isPasswordValid) {
      setError("Please create a stronger password");
      setLoading(false);
      return;
    }

    if (!doPasswordsMatch) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    try {
      await resetPassword({ token, password, confirmPassword }).unwrap();
      setIsSuccess(true);
      notify.success("Your password has been successfully reset");

      setTimeout(() => {
        globalThis.location.href = "/";
      }, 3000);
    } catch {
      setError(
        "Failed to reset password. Please try again or request a new reset link.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <Container>
        <ContentWrapper>
          <CardSection>
            <Card elevation={0}>
              <BrandSection>
                <BrandIcon>
                  <Cancel fontSize="large" color="error" />
                </BrandIcon>
                <Typography
                  variant="h4"
                  color="error"
                  sx={{ fontWeight: "bold" }}
                  gutterBottom
                >
                  Invalid Link
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  This password reset link has expired or is no longer valid.
                </Typography>
              </BrandSection>

              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Password reset links expire after 24 hours for security
                  reasons.
                </Typography>

                <Button
                  type={ButtonTypes.primary}
                  isFullWidth={true}
                  size="large"
                  onClick={() =>
                    (globalThis.location.href = "/forgot-password")
                  }
                >
                  Request New Reset Link
                </Button>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  href="/"
                  underline="hover"
                  sx={{
                    fontWeight: "medium",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    color: "var(--color-primary)",
                  }}
                >
                  <ArrowBack fontSize="small" />
                  Back to Sign In
                </Link>
              </Box>
            </Card>
          </CardSection>
        </ContentWrapper>
      </Container>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <Container>
        <ContentWrapper>
          <CardSection>
            <Card elevation={0}>
              <BrandSection>
                <BrandIcon>
                  <CheckCircle fontSize="large" />
                </BrandIcon>
                <Typography
                  variant="h4"
                  color="success.main"
                  sx={{ fontWeight: "bold" }}
                  gutterBottom
                >
                  Password Reset Complete
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Your password has been successfully updated. You can now sign
                  in with your new password.
                </Typography>
              </BrandSection>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Redirecting you to sign in...
                </Typography>

                <Button
                  type={ButtonTypes.primary}
                  isFullWidth={true}
                  size="large"
                  onClick={() => (globalThis.location.href = "/")}
                >
                  Continue to Sign In
                </Button>
              </Box>
            </Card>
          </CardSection>
        </ContentWrapper>
      </Container>
    );
  }

  // Main form state
  return (
    <Container>
      <ContentWrapper>
        <CardSection>
          <Card elevation={0}>
            <BrandSection>
              <BrandIcon>
                <img src={logo} alt="NWT" />
              </BrandIcon>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "var(--color-primary)" }}
                gutterBottom
              >
                Set New Password
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create a strong password for your account
              </Typography>
            </BrandSection>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <StyledTextField
                fullWidth
                label="New Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
                autoFocus
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {password && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Password Strength:
                    </Typography>
                    <Chip
                      label={passwordStrength.label}
                      color={passwordStrength.color}
                      size="small"
                    />
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={(passwordStrength.score / 4) * 100}
                    color={passwordStrength.color}
                    sx={{ mb: 1, borderRadius: 1 }}
                  />

                  {passwordStrength.feedback.length > 0 && (
                    <Box>
                      {passwordStrength.feedback.map((item) => (
                        <Typography
                          key={item}
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          • {item}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <StyledTextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
                error={confirmPassword.length > 0 && !doPasswordsMatch}
                helperText={
                  confirmPassword.length > 0 && !doPasswordsMatch
                    ? "Passwords don't match"
                    : ""
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          disabled={loading}
                          aria-label={
                            showConfirmPassword
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                        {confirmPassword && doPasswordsMatch && (
                          <CheckCircle
                            color="success"
                            fontSize="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <div style={{ marginBottom: "2rem" }}>
                <Button
                  buttonType="submit"
                  type={ButtonTypes.primary}
                  isFullWidth={true}
                  size="large"
                  isLoading={loading}
                  disabled={!isPasswordValid || !doPasswordsMatch}
                >
                  {loading ? "Updating Password..." : "Update Password"}
                </Button>
              </div>
            </form>

            <Box sx={{ textAlign: "center" }}>
              <Link
                href="/"
                underline="hover"
                sx={{
                  fontWeight: "medium",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  color: "var(--color-primary)",
                }}
              >
                <ArrowBack fontSize="small" />
                Back to Sign In
              </Link>
            </Box>
          </Card>
        </CardSection>
      </ContentWrapper>
    </Container>
  );
};
